import { motion } from 'framer-motion';
import { useState } from 'react';

const BooksList = () => {
  const [selectedBook, setSelectedBook] = useState<number | null>(null);

  const books = [
    {
      id: 1,
      title: 'Giving is Not Just For The Very Rich',
      subtitle: 'A How-To Guide For Giving And Philanthropy',
      year: '2024',
      pages: 'Kindle Edition',
      isbn: 'ASIN: B0CW2X3KYQ',
      description: 'A comprehensive guide to philanthropy and giving that demonstrates how charitable giving is accessible to everyone, not just the wealthy. Dr. Gitelson draws on her extensive experience in international consulting and philanthropy to provide practical advice and inspiration for individuals at all income levels who want to make a meaningful difference through charitable giving.',
      themes: ['Philanthropy', 'Charitable Giving', 'Social Impact', 'Personal Finance'],
      cover: 'https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/8b4fc084e486ee6de006b92086725c3a.jpeg',
      reviews: [
        { source: 'Amazon Readers', quote: 'An inspiring and practical guide to making a difference' },
        { source: 'Philanthropy Community', quote: 'Essential reading for anyone interested in charitable giving' },
      ],
      amazonLink: 'https://www.amazon.com/Giving-Just-Very-Rich-How/dp/196897010X/ref=monarch_sidesheet_title',
    },
  ];

  return (
    <section className="py-24 px-8 lg:px-16 bg-white" data-product-shop>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {books.map((book, index) => (
            <motion.div
              key={book.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-light-cream rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 p-8">
                {/* Book Cover */}
                <div className="md:col-span-2">
                  <div className="rounded-lg overflow-hidden shadow-xl">
                    <img
                      src={book.cover}
                      alt={book.title}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>

                {/* Book Details */}
                <div className="md:col-span-3 flex flex-col">
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-burgundy/10 text-burgundy text-xs font-lato tracking-wider rounded-full">
                      {book.year}
                    </span>
                  </div>

                  <h3 className="font-playfair text-3xl font-bold text-charcoal mb-2">
                    {book.title}
                  </h3>
                  <p className="font-lato text-sm text-warm-gray mb-4">{book.subtitle}</p>

                  <p className="font-lato text-sm text-warm-gray leading-relaxed mb-4 line-clamp-4">
                    {book.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {book.themes.map((theme, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-white text-burgundy text-xs font-lato rounded-full border border-burgundy/20"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 border-t border-warm-gray/20">
                    <div className="flex items-center justify-between text-xs text-warm-gray mb-4">
                      <span>{book.pages}</span>
                      <span>{book.isbn}</span>
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => setSelectedBook(selectedBook === book.id ? null : book.id)}
                        className="w-full px-6 py-3 bg-burgundy text-cream font-lato text-sm tracking-wider rounded-lg hover:bg-burgundy/90 transition-all cursor-pointer whitespace-nowrap"
                      >
                        {selectedBook === book.id ? 'HIDE DETAILS' : 'VIEW DETAILS'}
                      </button>
                      
                      {book.amazonLink && (
                        <a
                          href={book.amazonLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full px-6 py-3 bg-white text-burgundy font-lato text-sm tracking-wider rounded-lg border-2 border-burgundy hover:bg-burgundy hover:text-cream transition-all text-center cursor-pointer whitespace-nowrap"
                        >
                          VIEW ON AMAZON
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedBook === book.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-8 pb-8 border-t border-warm-gray/10"
                >
                  <div className="pt-6">
                    <h4 className="font-playfair text-xl font-bold text-charcoal mb-4">
                      Full Description
                    </h4>
                    <p className="font-lato text-warm-gray leading-relaxed mb-6">
                      {book.description}
                    </p>

                    <h4 className="font-playfair text-xl font-bold text-charcoal mb-4">
                      Critical Acclaim
                    </h4>
                    <div className="space-y-4">
                      {book.reviews.map((review, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4">
                          <p className="font-lato text-charcoal italic mb-2">"{review.quote}"</p>
                          <p className="font-lato text-sm text-burgundy">— {review.source}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BooksList;
